import { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  tertiaryText,
  semiBoldText,
  largeTextSize,
  xxxLargeTextSize,
  boldText,
  smallTextSize,
  mediumText,
  baseTextSize,
  extraLargeTextSize,
} from '../../styling/stylingHelper';
import { currencyFormatter } from '../../services/helperService';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppSettingsStore } from '../../stores/appSettingsStore';

/**
 * Formats a numeric KPI value. Returns '--' for 0/null/undefined so empty
 * metrics read as "no data" rather than "0". Shared across all metric cards.
 */
export function formatMainValue(
  val: number,
  valueType: 'currency' | 'percentage' | 'number' | 'blueNumber' | 'bluePercentage' | 'greenPercentage' | 'orangePercentage'
) {
  if (val === undefined || val === null || val === 0) return '--';
  if (valueType === 'currency') return currencyFormatter.format(val);
  else if (valueType === 'percentage' || valueType === 'bluePercentage' || valueType === 'greenPercentage' || valueType === 'orangePercentage')
    return `${val.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  else if (valueType === 'number' || valueType === 'blueNumber')
    return `${val.toFixed(0)}`;
  else return '--';
}

export interface StatCardProps {
  /** Icon node (lucide-react) rendered inside the 42×42 colored tile. */
  icon: ReactNode;
  /** Hex+alpha tint for the icon tile background (see `iconBg*` in stylingHelper). */
  iconBg: string;
  title: string;
  value: string | number;
  /** Formats a numeric `value`. Ignored when `value` is already a string. */
  valueType?: 'currency' | 'percentage' | 'number';
  loading?: boolean;
  changeValue?: number;
  changeLabel?: string;
  /** Which direction is "good" — drives the +/- pill color. Default 'up'. */
  trendDirection?: 'up' | 'down';
  subtitle?: ReactNode;
  /** Generic bottom slot (e.g. food/drinks SplitLegend). */
  bottom?: ReactNode;
  /** When set, the whole card is clickable (cursor-pointer + hover). */
  onClick?: () => void;
  className?: string;
  /** Extra classes for the big value span (e.g. a red tone for negative leakage). */
  valueClassName?: string;
  /** When true, show a right chevron in the header (e.g. drill-in affordance). */
  showArrow?: boolean;
  /**
   * Compact phone (<sm) layout, per the designer mock. 'row' (default) = full-width
   * card: icon + title/value left, change pill + period right — for stacked KPI
   * columns of ≤3 cards. 'tile' = icon on top, title/value below — for 2-col
   * `grid-cols-2` KPI grids of 4 cards. Desktop (≥sm) is identical either way.
   */
  mobileLayout?: 'row' | 'tile';
}

/** Shared percentage-change pill for KPI cards and detailed metric tables. */
export const KpiChangeBadge = ({ changeValue, trendDirection = 'up', title, compact = false }: {
  changeValue: number;
  trendDirection?: 'up' | 'down';
  title?: string;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore((s) => s.darkMode);

  // A change is "good" when it moves in the desired direction: up for revenue,
  // down for cost%. Good → green pill, bad → red pill (matches the UI mock).
  const isGoodChange =
    typeof changeValue === 'number' && changeValue !== 0
      ? changeValue > 0
        ? trendDirection === 'up'
        : trendDirection === 'down'
      : null;

  // Green/red tinted pill (10% alpha bg + solid text), mirroring the mock.
  const pillStyle =
    isGoodChange === null
      ? undefined
      : isGoodChange
      ? { backgroundColor: darkMode ? '#34D3991A' : '#069A6E1A', color: darkMode ? '#34D399' : '#069A6E' }
      : { backgroundColor: darkMode ? '#FCA5A51A' : '#D92D201A', color: darkMode ? '#FCA5A5' : '#D92D20' };

  // Signed percentage shown inside the pill (e.g. "+8.4%", "-2.4%").
  const changeText =
    typeof changeValue === 'number'
      ? `${changeValue > 0 ? '+' : changeValue < 0 ? '-' : ''}${Math.abs(
          changeValue
        ).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
      : '';

  return (
    <span
      className={cn(
        'rounded-md whitespace-nowrap',
        compact ? 'text-xs font-medium px-1.5 py-0.5' : cn(smallTextSize, semiBoldText, 'px-2 py-1')
      )}
      style={pillStyle}
      aria-label={changeValue > 0 ? t('common.upTrend') : t('common.downTrend')}
      title={title}
    >
      {changeText}
    </span>
  );
};

export const StatCard = ({
  icon,
  iconBg,
  title,
  value,
  valueType,
  loading = false,
  changeValue,
  changeLabel,
  trendDirection = 'up',
  subtitle,
  bottom,
  onClick,
  className,
  valueClassName,
  showArrow = false,
  mobileLayout = 'row',
}: StatCardProps) => {
  const displayValue =
    typeof value === 'number'
      ? formatMainValue(value, valueType ?? 'number')
      : value;

  // Hide the change pill when there's no value to compare against (renders as '--').
  // A "-100%" next to an empty metric is noise, not signal.
  const showChange = typeof changeValue === 'number' && changeValue !== 0 && displayValue !== '--';

  // One pill node reused by the desktop and compact-mobile layouts.
  const changePill = showChange ? (
    <KpiChangeBadge changeValue={changeValue as number} trendDirection={trendDirection} />
  ) : null;

  return (
    <div
      className={cn(
        cardClass,
        'p-3 sm:p-5 flex flex-col h-full',
        onClick && 'cursor-pointer hover:shadow transition-shadow',
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Header: icon tile + title (+ optional right chevron) — desktop/tablet only */}
      <div className={cn('hidden sm:flex items-center gap-3', showArrow && 'justify-between')}>
        <div className="flex items-center gap-3">
          <span
            className="flex items-center justify-center w-[42px] h-[42px] rounded-[8px] flex-shrink-0"
            style={{ backgroundColor: iconBg }}
          >
            {icon}
          </span>
          <span className={cn(secondaryText, largeTextSize, semiBoldText)}>
            {title}
          </span>
        </div>
        {showArrow ? (
          <span className={cn(tertiaryText, 'ml-4 flex-shrink-0')} aria-hidden>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : null}
      </div>

      {/* Value, change pill and period label share one flex-wrap row so the layout
          collapses responsively as the card narrows: all inline → value+change with
          the label wrapping below → value alone with change+label wrapping below. */}
      <div className="hidden sm:block mt-5">
        {loading ? (
          <LoaderCircle className={cn('h-8 w-8 animate-spin', primaryText)} />
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className={cn(primaryText, xxxLargeTextSize, boldText, valueClassName)}>
                {displayValue}
              </span>
              {changePill}
              {showChange && changeLabel ? (
                <span
                  className={cn(smallTextSize, mediumText, tertiaryText, 'whitespace-nowrap')}
                >
                  {changeLabel}
                </span>
              ) : null}
            </div>

            {!showChange && subtitle ? (
              <div className={cn(smallTextSize, mediumText, tertiaryText, 'mt-1')}>
                {subtitle}
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Compact phone layout (designer mock), <sm only. Same icon, 32px tile. */}
      {mobileLayout === 'row' ? (
        <div className="sm:hidden flex items-stretch gap-3">
          <span
            className="flex items-center justify-center w-8 h-8 rounded-[8px] flex-shrink-0 self-center"
            style={{ backgroundColor: iconBg }}
          >
            {icon}
          </span>
          <div className="flex flex-col justify-between flex-1 min-w-0 gap-1">
            <span className={cn(secondaryText, baseTextSize, semiBoldText, 'truncate')}>{title}</span>
            {loading ? (
              <LoaderCircle className={cn('h-5 w-5 animate-spin', primaryText)} />
            ) : displayValue !== '' ? (
              <span className={cn(primaryText, extraLargeTextSize, boldText, valueClassName)}>
                {displayValue}
              </span>
            ) : null}
          </div>
          {!loading && (showChange || subtitle) ? (
            <div className="flex flex-col items-end justify-center gap-1 flex-shrink-0">
              {changePill}
              {showChange && changeLabel ? (
                <span className={cn(smallTextSize, mediumText, tertiaryText, 'whitespace-nowrap')}>
                  {changeLabel}
                </span>
              ) : !showChange && subtitle ? (
                <span className={cn(smallTextSize, mediumText, tertiaryText)}>{subtitle}</span>
              ) : null}
            </div>
          ) : null}
          {showArrow ? (
            <span className={cn(tertiaryText, 'flex items-center flex-shrink-0')} aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          ) : null}
        </div>
      ) : (
        <div className="sm:hidden flex flex-col">
          <div className="flex items-center justify-between">
            <span
              className="flex items-center justify-center w-8 h-8 rounded-[8px] flex-shrink-0"
              style={{ backgroundColor: iconBg }}
            >
              {icon}
            </span>
            {showArrow ? (
              <span className={cn(tertiaryText, 'flex-shrink-0')} aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            ) : null}
          </div>
          <span className={cn(secondaryText, baseTextSize, semiBoldText, 'mt-2')}>{title}</span>
          {loading ? (
            <LoaderCircle className={cn('h-5 w-5 animate-spin mt-3', primaryText)} />
          ) : displayValue !== '' || showChange || subtitle ? (
            <div className="mt-3 flex flex-col gap-1">
              {displayValue !== '' ? (
                <span className={cn(primaryText, extraLargeTextSize, boldText, valueClassName)}>
                  {displayValue}
                </span>
              ) : null}
              <div className="flex items-center gap-2 flex-wrap">
                {changePill}
                {showChange && changeLabel ? (
                  <span className={cn(smallTextSize, mediumText, tertiaryText, 'whitespace-nowrap')}>
                    {changeLabel}
                  </span>
                ) : !showChange && subtitle ? (
                  <span className={cn(smallTextSize, mediumText, tertiaryText)}>{subtitle}</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Generic bottom slot */}
      {bottom && !loading ? <div className="hidden sm:block mt-4">{bottom}</div> : null}
      {bottom && !loading ? <div className="sm:hidden mt-3">{bottom}</div> : null}
    </div>
  );
};
